/**
 * Merge body radii and jar wall thickness for a set of flask bounds. Pure math, shared by
 * Game#getFlaskSystem (it builds the physics from these numbers) and the loader warm-up
 * (core/play-warmup.js bakes the creatures at the size the jar will show them), so both read
 * exactly the same radii. Radii follow C.FLASK_EVOLUTION_SIZING (flask/evolution-sizing.js);
 * the legacy ratio table is the fallback when that sizing does not apply.
 */
import * as C from "../config/constants.js";
import { computeEvolutionRadii } from "./evolution-sizing.js";

var DEFAULT_RADIUS_RATIOS = [0.00424, 0.00568, 0.00712, 0.00856, 0.00992, 0.01136, 0.0128, 0.01424, 0.01568, 0.01704, 0.01848, 0.01992, 0.02136, 0.02272, 0.02416];

/**
 * @param {{ left: number, right: number, top: number, bottom: number }} bounds flask bounds
 * @param {number} chainLength active evolution chain length (tiers that get a radius)
 * @returns {{ bodyRadiusByTier: number[], mergeFlashRadius: number, flaskWallThickness: number }}
 */
export function computeFlaskBodySizing(bounds, chainLength) {
  var w = bounds.right - bounds.left;
  var h = bounds.bottom - bounds.top;
  var paddingRatio = (typeof window !== "undefined" && C.FLASK_PADDING_RATIO != null) ? C.FLASK_PADDING_RATIO : 0.05;
  var legacyCutoff = (typeof window !== "undefined" && C.FLASK_CUTOFF_FRACTION != null) ? C.FLASK_CUTOFF_FRACTION : 0.9;
  var cutoffTopFraction = (typeof window !== "undefined" && C.FLASK_CUTOFF_TOP_FRACTION != null)
    ? C.FLASK_CUTOFF_TOP_FRACTION : legacyCutoff;
  var cutoffBottomFraction = (typeof window !== "undefined" && C.FLASK_CUTOFF_BOTTOM_FRACTION != null)
    ? C.FLASK_CUTOFF_BOTTOM_FRACTION : legacyCutoff;
  cutoffTopFraction = Math.max(0.05, Number(cutoffTopFraction) || legacyCutoff);
  cutoffBottomFraction = Math.max(0.05, Number(cutoffBottomFraction) || legacyCutoff);
  var flaskImageAspect = (typeof window !== "undefined" && C.FLASK_IMAGE_ELLIPSE_ASPECT != null)
    ? C.FLASK_IMAGE_ELLIPSE_ASPECT : 1.0;
  flaskImageAspect = Math.max(0.2, Number(flaskImageAspect) || 1.0);
  var flaskBodyRadiusRatio = (typeof window !== "undefined" && C.FLASK_IMAGE_BODY_RADIUS_RATIO != null)
    ? C.FLASK_IMAGE_BODY_RADIUS_RATIO : 1.0;
  flaskBodyRadiusRatio = Math.max(0.1, Number(flaskBodyRadiusRatio) || 1.0);
  var padding = Math.min(w, h) * paddingRatio;
  var rMaxWidth = (w - 2 * padding) / 2;
  var rMaxHeight = (h - 2 * padding) / (cutoffTopFraction + cutoffBottomFraction);
  var ryMaxByWidth = rMaxWidth / flaskImageAspect;
  var ellipseRyBase = Math.max(0, Math.min(ryMaxByWidth, rMaxHeight));
  var ellipseRy = ellipseRyBase * flaskBodyRadiusRatio;
  var ellipseRx = ellipseRy * flaskImageAspect;
  var sphereR = Math.max(0, Math.min(ellipseRx, ellipseRy));
  var effectiveDiameter = 2 * sphereR;
  var ratios = (typeof window !== "undefined" && C.FLASK_BODY_RADIUS_RATIOS) || DEFAULT_RADIUS_RATIOS;
  var flaskScale = (typeof window !== "undefined" && C.FLASK_MERGE_SCALE != null) ? C.FLASK_MERGE_SCALE : 1.0;
  var bodyRadiusByTier = ratios.slice(0, chainLength).map(function (r) { return Math.max(4, effectiveDiameter * r * flaskScale); });
  var wallRatio = (typeof window !== "undefined" && C.FLASK_WALL_THICKNESS_BOUNDS_RATIO != null)
    ? Number(C.FLASK_WALL_THICKNESS_BOUNDS_RATIO)
    : 0.045;
  if (!isFinite(wallRatio) || wallRatio <= 0) wallRatio = 0.045;
  var flaskWallThickness = Math.max(4, Math.min(28, Math.min(w, h) * wallRatio));
  // Usable jar: between the inner wall faces (flask/system/geometry.js neckLeft/neckRight),
  // rim to floor (the floor sits below bounds.bottom).
  var chain = (C.EVOLUTION_CHAIN || []).slice(0, chainLength);
  var evolutionRadii = computeEvolutionRadii(
    chain,
    w - 2 * flaskWallThickness,
    h,
    C.FLASK_EVOLUTION_SIZING
  );
  return {
    bodyRadiusByTier: evolutionRadii || bodyRadiusByTier,
    mergeFlashRadius: effectiveDiameter * 0.06,
    flaskWallThickness: flaskWallThickness,
  };
}
